import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../lib/cn.js';
/**
 * A surface with a rank.
 *
 * `Card` renders exactly one recipe, `rounded-md border border-border-subtle bg-bg-raised`, and the
 * report page draws that same recipe around 107 times. The consequence is that "Extracted C2 config"
 * and "Suricata alerts: 0" are pixel-identical containers, so the page has no hierarchy and the
 * reader has no idea where to look. Adding shadows would not fix it; the missing thing is a way to
 * SAY that one panel outranks another.
 *
 * Two independent axes, because they answer different questions:
 *
 *   `tone`  what this panel MEANS      neutral | notable | danger | success | warning
 *   `emphasis` how much it should SHOUT  flat | raised | floating
 *
 * A danger panel is not automatically loud (a resolved warning can be quiet) and a loud panel is
 * not automatically dangerous (the headline verdict is loud and might be clean), so collapsing
 * these into one prop would immediately produce the wrong combinations.
 *
 * Tone is carried by a left rail rather than a full tint. A tinted fill behind a dense table of
 * hashes hurts legibility and fights the verdict colours inside the panel; a 2px rail reads
 * instantly at a glance and costs the content nothing.
 */
const panelVariants = cva('relative rounded-md transition-colors motion-reduce:transition-none', {
    variants: {
        tone: {
            neutral: 'border border-border-subtle',
            notable: 'border border-accent-500/30',
            danger: 'border border-status-danger/30',
            success: 'border border-verdict-clean/30',
            warning: 'border border-status-warning/30',
        },
        emphasis: {
            /** Sits ON the page. For secondary detail that should recede. */
            flat: 'bg-transparent',
            /** The default working surface. */
            raised: 'bg-bg-raised',
            /** Lifted off the page. For the one thing on a screen that matters most. */
            floating: 'bg-bg-overlay shadow-sm',
        },
        /** Denser padding for panels that wrap a table, which brings its own row rhythm. */
        density: { comfortable: '', compact: '' },
    },
    defaultVariants: { tone: 'neutral', emphasis: 'raised', density: 'comfortable' },
});
const RAIL = {
    notable: 'bg-accent-500',
    danger: 'bg-status-danger',
    success: 'bg-verdict-clean',
    warning: 'bg-status-warning',
};
export const Panel = React.forwardRef(({ className, tone, emphasis, density, rail = true, children, ...rest }, ref) => {
    const railColor = tone && tone !== 'neutral' ? RAIL[tone] : undefined;
    return (_jsxs("div", { ref: ref, className: cn(panelVariants({ tone, emphasis, density }), railColor && rail && 'overflow-hidden', className), ...rest, children: [railColor && rail && (_jsx("span", { "aria-hidden": true, className: cn('absolute inset-y-0 left-0 w-0.5', railColor) })), children] }));
});
Panel.displayName = 'Panel';
/**
 * Panel header.
 *
 * `CardTitle` is always `text-lg font-semibold`, which is why every panel on the report page shouts
 * equally. Here the heading is body-sized and separated by weight, with `size="lg"` reserved for the
 * few panels that genuinely lead a screen. `actions` exists so controls stop being crammed into the
 * content area under their own heading.
 */
export function PanelHeader({ title, description, actions, count, size = 'md', className, ...rest }) {
    return (_jsxs("div", { className: cn('flex items-start justify-between gap-3 border-b border-border-subtle px-4 py-3', className), ...rest, children: [_jsxs("div", { className: "min-w-0", children: [_jsxs("h3", { className: cn('flex items-baseline gap-2 font-semibold text-text-primary', size === 'lg' ? 'text-h3' : 'text-section'), children: [_jsx("span", { className: "truncate", children: title }), count !== undefined && (_jsx("span", { className: "shrink-0 font-mono text-micro font-normal text-text-muted", children: count }))] }), description && _jsx("p", { className: "mt-1 text-sm leading-relaxed text-text-secondary", children: description })] }), actions && _jsx("div", { className: "flex shrink-0 items-center gap-2", children: actions })] }));
}
export function PanelBody({ className, ...rest }) {
    return _jsx("div", { className: cn('px-4 py-3', className), ...rest });
}
//# sourceMappingURL=panel.js.map